import type { RoutePathOption } from '@gpx-spa/geo-domain'

const LOW_TOTAL = 15_000
const HIGH_TOTAL = 45_000
const BUCKET = 3_000
const MIN_QUALITY = 90

/**
 * Geometry quality % from **sum** of vertex counts across all path alternatives (full polylines).
 *
 * - **≤ 15 000** → 100%
 * - **> 45 000** → 90%
 * - **Between:** −1 percentage point per full **3 000** vertices above 15 000 (so from 100% down to 90% across the 15k–45k band).
 */
export function suggestedGeometryQualityFromTotalVertices(total: number): number {
  if (total <= LOW_TOTAL) return 100
  if (total > HIGH_TOTAL) return MIN_QUALITY
  const steps = Math.floor((total - LOW_TOTAL) / BUCKET)
  return 100 - Math.min(10, steps)
}

export type GeometryQualitySuggestion = {
  /** Sum of raw polyline vertex counts across all path alternatives. */
  totalVertices: number
  /** Turf simplify tolerance band (100 = minimal simplification). */
  percent: number
}

/**
 * Sums per-path vertex counts, then {@link suggestedGeometryQualityFromTotalVertices}.
 */
export function suggestedGeometryQualityDetailFromPaths(
  paths: RoutePathOption[]
): GeometryQualitySuggestion {
  let total = 0
  for (const p of paths) {
    const c = p.coordinates
    if (!c || c.length < 2) continue
    total += c.length
  }
  const percent = total === 0 ? 100 : suggestedGeometryQualityFromTotalVertices(total)
  return { totalVertices: total, percent }
}

/**
 * Same as {@link suggestedGeometryQualityDetailFromPaths} but returns only the percent.
 */
export function suggestedGeometryQualityPercentFromPaths(paths: RoutePathOption[]): number {
  return suggestedGeometryQualityDetailFromPaths(paths).percent
}
